import { createElement, useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { DocumentUpdateModel } from "../../../common/documents/documentModel";
import { deleteHttp, post, put, sendWithFile } from "../../../common/api/apiShared";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { TaskDocumentationViewModel, TaskDocumentDescriptionViewModel, TaskDocumentSaveModel } from "./TaskDocumentation.types";
import { ITaskDocumentation } from "./TaskDocumentation";
import { useDotLegalQueryClient, useDotLegalMutation, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "../../../localization/useTranslation";
import { useFeatureFlags } from "../../../auth/planProvider/PlanProvider";
import { FeatureFlag } from "../../../auth/planProvider/PlanProvider.types";
import { isNullOrWhitespace } from "@common/stringOperations";

// Mirrors the backend: the image types Mistral vision accepts (Core/Infrastructure/ImageMimeTypes.cs) plus the
// document types the handler sends as extracted text. Not the preview detector, which also allows bmp.
const describableExtensions = ["png", "jpg", "jpeg", "gif", "webp", "pdf", "docx"];

export function canDescribeDocument(document: TaskDocumentationViewModel): boolean {
    if (document.isPolicy || isNullOrWhitespace(document.dataId)) {
        return false;
    }
    const extension = document.name?.split(".").pop()?.toLowerCase();
    return extension !== undefined && describableExtensions.includes(extension);
}

export function useTaskDocumentationHooks(props: ITaskDocumentation, updateDocumentState: (hasDocuments: boolean) => void) {
    const snackbar = useDotLegalSnackbar();
    const featureFlags = useFeatureFlags();

    const [documentationOption, setDocumentationOption] = useState(1);
    const [taskLink, setTaskLink] = useState<string | undefined>(undefined);
    const [selectedDocument, setSelectedDocument] = useState<TaskDocumentationViewModel | undefined>(undefined);
    const { translateString } = useTranslation();
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();

    const taskId = props.taskId;
    const queryKey = "documents" + taskId;
    let documentsQuery = useDotLegalQueryWithKey<Array<TaskDocumentationViewModel>>(queryKey, `/taskDocumentation/${taskId}`);

    const createDocumentMutation = useDotLegalMutation((upload: { document: TaskDocumentSaveModel; file: File | undefined }) =>
        sendWithFile("POST", `/task/${taskId}/documents`, upload.document, upload.file, trackUploadProgress())
    );
    const queryClient = useDotLegalQueryClient();

    useEffect(() => {
        if (props.passIsDoneLoading && documentsQuery.isSuccess) {
            props.passIsDoneLoading(true);
        }
    }, [documentsQuery.isSuccess, props]);

    const removeMutation = useDotLegalMutation(removePolicyAPI);

    const removePolicy = (id: string) => {
        removeMutation.mutateAsync(id, {
            onSuccess: (response: any) => {
                snackbar.show(translateString("policyRemovedDeletedTask"));
                updateDocumentState(documentsQuery.data!.filter((q) => q.id !== id).length > 0);
                documentsQuery.refetch();
            },
        });
    };

    async function removePolicyAPI(id: string) {
        return await deleteHttp<string>(`/task/${props.taskId}/policies/${id}`);
    }

    const removeDocumentMutation = useDotLegalMutation((documentId: string) => deleteHttp(`/task/${taskId}/documents/${documentId}`), {
        onMutate: async (documentId) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries(queryKey);
            updateDocumentState(documentsQuery.data!.filter((q) => q.id !== documentId).length > 0);
            queryClient.setQueryData<Array<TaskDocumentationViewModel>>(queryKey, (old) => old!.filter((o) => o.id !== documentId));
        },
    });

    const updateDocumentMutation = useDotLegalMutation((updateModel: DocumentUpdateModel) => put(`/task/${taskId}/documents/${updateModel.id}`, updateModel), {
        onMutate: async (updateModel) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries(queryKey);
            queryClient.setQueryData<Array<TaskDocumentationViewModel>>(queryKey, (old) =>
                old!.map((document) => (document.id === updateModel.id ? { ...document, name: updateModel.fileName, link: updateModel.link } : document))
            );
        },
    });

    const describeDocumentMutation = useDotLegalMutation(
        (documentId: string) => post<TaskDocumentDescriptionViewModel>(`/task/${taskId}/documents/${documentId}/describe`, undefined),
        {
            onSuccess: (result, documentId) => {
                if (!result.success()) {
                    return;
                }
                const fileName = result.value().fileName;
                queryClient.setQueryData<Array<TaskDocumentationViewModel>>(queryKey, (old) =>
                    old!.map((document) => (document.id === documentId ? { ...document, name: fileName } : document))
                );
                setSelectedDocument(queryClient.getQueryData<Array<TaskDocumentationViewModel>>(queryKey)?.find((document) => document.id === documentId));
            },
        }
    );

    const createDocuments = async (files: Array<DotLegalFile> | undefined) => {
        if (files) {
            for (const f of files) {
                await createDocumentMutation.mutateAsync({ document: { fileName: f.fileName, link: undefined }, file: f.file });
            }
            await documentsQuery.refetch();
            if (files.length === 1) {
                let documents = queryClient.getQueryData<Array<TaskDocumentationViewModel>>(queryKey);
                var latestDocument = documents?.[documents.length - 1];
                setSelectedDocument(latestDocument);

                snackbar.show(createElement(Trans, { i18nKey: "documentUploaded", values: { document: files[0].fileName } }));
            } else {
                snackbar.show(createElement(Trans, { i18nKey: "multipleDocumentsUploaded", values: { count: files.length } }));
            }
        }

        if (props.onFileDialogClose) {
            props.onFileDialogClose();
        }
    };

    const removeDocument = (documentId: string) => {
        removeDocumentMutation.mutateAsync(documentId);
        const document = documentsQuery.data?.find((d) => d.id === documentId);
        snackbar.show(createElement(Trans, { i18nKey: "documentDeleted", values: { document: document?.name ?? document?.link } }));
    };

    const updateDocument = (updateModel: DocumentUpdateModel) => {
        updateDocumentMutation.mutateAsync(updateModel);
    };

    async function uploadLink() {
        if (taskLink) {
            await createDocumentMutation.mutateAsync({ document: { fileName: undefined, link: taskLink }, file: undefined });
            await documentsQuery.refetch();
            snackbar.show(createElement(Trans, { i18nKey: "linkUploaded", values: { link: taskLink } }));
            setTaskLink(undefined);
        }
    }

    return {
        documentsData: documentsQuery.data,
        createDocuments: createDocuments,
        removeDocument: removeDocument,
        documentsUploading: createDocumentMutation.isLoading,
        uploadPercentage,
        documentationOption,
        setDocumentationOption,
        taskLink,
        setTaskLink,
        uploadLink,
        updateDocument,
        selectedDocument,
        setSelectedDocument,
        refetch: documentsQuery.refetch,
        removePolicy,
        describeDocument: (documentId: string) => describeDocumentMutation.mutateAsync(documentId),
        describingDocumentId: describeDocumentMutation.isLoading ? describeDocumentMutation.variables : undefined,
        showAiDescribe: featureFlags.isEnabled(FeatureFlag.AiDocumentDescription),
    };
}
